import { useEffect, useState } from "react";
import fixWebmDuration from "webm-duration-fix";

import { DEFAULT_MEDIA_OPTIONS } from "./utils/default-media-options";

export type ScreenRecordingStatus = "STARTED" | "TOO_SHORT" | "SAVED";

const mimeType = `video/webm;codecs=vp9`;

const saveFile = async (recordedChunks: BlobEvent["data"][]) => {
  const buggedBlob = new Blob(recordedChunks, { type: mimeType });
  const fixedBlob = await fixWebmDuration(buggedBlob);

  const filename = window.prompt("Enter file name"),
    downloadLink = document.createElement("a");
  if (!filename) return;
  downloadLink.href = URL.createObjectURL(fixedBlob);
  downloadLink.download = `${filename}.webm`;

  document.body.appendChild(downloadLink);
  downloadLink.click();

  URL.revokeObjectURL(downloadLink.href); // clear from memory
  document.body.removeChild(downloadLink);
};

const getRecordScreenPermission = async () =>
  await navigator.mediaDevices.getDisplayMedia(DEFAULT_MEDIA_OPTIONS);

const defaultStatusUpdateFunction = (status: ScreenRecordingStatus): void => undefined;
export const useScreenRecorder = (statusUpdateFunction = defaultStatusUpdateFunction) => {
  const [recording, setRecording] = useState(false);
  const [recorder, setRecorder] = useState<MediaRecorder | null>();

  const createRecorder = async () => {
    try {
      const stream = await getRecordScreenPermission();
      const mediaRecorder = new MediaRecorder(stream, { mimeType });

      let recordedChunks: BlobEvent["data"][] = [];
      mediaRecorder.ondataavailable = ({ data }) => {
        if (data && data.size > 0) recordedChunks.push(data);
      };
      mediaRecorder.onstop = async () => {
        setRecording(false);
        if (recordedChunks.length < 1) return statusUpdateFunction("TOO_SHORT");

        await saveFile(recordedChunks);

        statusUpdateFunction("SAVED");
        recordedChunks = [];
      };

      statusUpdateFunction("STARTED");
      mediaRecorder.start(200); // For every 200ms the stream data will be stored in a separate chunk.

      setRecorder(mediaRecorder);
    } catch (e) {
      setRecording(false);
    }
  };

  useEffect(() => {
    if (recording) {
      if (!recorder || !recorder.stream.active) {
        createRecorder();
      } else {
        statusUpdateFunction("STARTED");
        recorder.start();
      }
    } else if (recorder) {
      recorder.stop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording]);

  const switchRecordingActive = () => setRecording(!recording);

  return { recording, switchRecordingActive };
};
